import BaseLayer from './BaseLayer';
import FeatureAccumulator from '../utils/feature-accumulator';
import { KeyedMarkerGroup } from '../utils/markers';
import { removeSourceAndLayers, addOrUpdateGeoJsonSource } from '../utils/maplibre-helpers';
import { padBounds, centroidOfGeometry } from '../utils/coords';
import { ACCUMULATOR_MAX_FEATURES, ACCUMULATOR_VIEWPORT_KEEP_FACTOR } from '../core/constants';

export function responseError(response, data) {
    const payloadError = data && data.error;
    const message = payloadError && typeof payloadError === 'object'
        ? payloadError.message
        : payloadError;
    const error = new Error(message || ('Request failed with status ' + response.status));
    error.status = response.status;
    error.payload = data || null;
    error.code = payloadError && typeof payloadError === 'object' ? payloadError.code || null : null;
    return error;
}

export default class GeoJsonFetchLayer extends BaseLayer {
    constructor(config = {}) {
        super(config);
        this.requestVersion = 0;
        this.accumulator = config.accumulate ? new FeatureAccumulator() : null;
    }

    getFeatureKey(feature) {
        return feature && feature.properties ? feature.properties.id : null;
    }

    getDatasetKey(data) {
        const mode = (data && data.mode) || (data && data.aggregated ? 'agg' : 'individual');
        return this.id + '|' + mode + '|' + this.getFilterFingerprint();
    }

    getFilterFingerprint() { return ''; }

    fingerprintFromQuery(query) {
        const params = new URLSearchParams(query || '');
        ['bbox', 'zoom', 'background', 'limit'].forEach((key) => params.delete(key));
        params.sort();
        return params.toString();
    }

    featureGeometryKey(feature) {
        const centroid = centroidOfGeometry(feature && feature.geometry);
        return centroid ? 'g:' + centroid[0].toFixed(6) + ',' + centroid[1].toFixed(6) : null;
    }

    getAccumulatorKeepBounds() {
        const map = this.getMap();
        if (!map) return null;
        try {
            return padBounds(map.getBounds(), ACCUMULATOR_VIEWPORT_KEEP_FACTOR);
        } catch (_error) {
            return null;
        }
    }

    resetAccumulator() {
        if (this.accumulator) this.accumulator.reset();
    }

    accumulate(data) {
        if (!this.accumulator || !data || data.aggregated === true || !Array.isArray(data.features)) return null;
        const result = this.accumulator.merge(this.getDatasetKey(data), data.features, (feature) => this.getFeatureKey(feature));
        this.accumulator.evict(this.getAccumulatorKeepBounds(), ACCUMULATOR_MAX_FEATURES);
        return { collection: this.accumulator.featureCollection(), replaced: result.replaced };
    }

    getAbortController() {
        return this.config.getAbortController ? this.config.getAbortController() : null;
    }

    setAbortController(controller) {
        if (this.config.setAbortController) this.config.setAbortController(controller);
    }

    abortPending() {
        const controller = this.getAbortController();
        if (controller) controller.abort();
        this.setAbortController(null);
    }

    ensureKeyedMarkerGroup() {
        if (!this._keyedMarkerGroup) this._keyedMarkerGroup = new KeyedMarkerGroup();
        const map = this.getMap();
        if (map && this._keyedMarkerGroup._map !== map) this._keyedMarkerGroup.addTo(map);
        return this._keyedMarkerGroup;
    }

    disposeKeyedMarkerGroup() {
        if (this._keyedMarkerGroup) {
            this._keyedMarkerGroup.remove();
            this._keyedMarkerGroup = null;
        }
    }

    diffKeyedMarkers(group, features) {
        const wanted = new Set();
        (features || []).forEach((feature) => {
            const raw = this.getFeatureKey(feature);
            if (raw == null || raw === '') return;
            const key = String(raw);
            wanted.add(key);
            if (!group.has(key)) {
                const marker = this.createFeatureMarker(feature);
                if (marker) group.set(key, marker);
            }
        });
        group.keys().forEach((key) => {
            if (!wanted.has(key)) group.delete(key);
        });
    }

    _finishRender(data, layer) {
        if (this.config.setLayer) this.config.setLayer(layer);
        if (this.config.onRendered) this.config.onRendered(data, layer, this);
    }

    renderCapacityMarkerData(data) {
        const map = this.getMap();
        if (!map) return null;
        // Accumulating successive 500-item windows would defeat the viewport budget.
        this.resetAccumulator();
        this.disposeKeyedMarkerGroup();
        const previous = this.config.getLayer ? this.config.getLayer() : null;
        if (previous && typeof previous.remove === 'function') previous.remove();
        if (this.renderParcelFeatures) this.renderParcelFeatures(data.aggregated ? [] : (data.parcel_features || []));
        const group = this.renderAggregatedData(data);
        this._finishRender(data, group);
        return group;
    }

    renderMarkerData(data) {
        const map = this.getMap();
        if (!map) return null;
        const acc = this.accumulate(data);
        if (!acc) {
            this.disposeKeyedMarkerGroup();
            const currentLayer = this.config.getLayer ? this.config.getLayer() : null;
            if (currentLayer && typeof currentLayer.remove === 'function') currentLayer.remove();
            removeSourceAndLayers(map, this.getSourceId(), this.getLayerIds());
            const legacyLayer = data && data.aggregated === true
                ? this.renderAggregatedData(data)
                : this.renderIndividualData(data);
            this._finishRender(data, legacyLayer);
            return legacyLayer;
        }
        const previous = this.config.getLayer ? this.config.getLayer() : null;
        if (previous && previous !== this._keyedMarkerGroup && typeof previous.remove === 'function') previous.remove();
        const group = this.ensureKeyedMarkerGroup();
        this.diffKeyedMarkers(group, acc.collection.features);
        this._finishRender(data, group);
        return group;
    }

    renderSourceData(data, addLayersFn) {
        const map = this.getMap();
        if (!map) return null;
        const acc = this.accumulate(data);
        const sourceId = this.getSourceId();
        const collection = acc ? acc.collection : { type: 'FeatureCollection', features: (data && data.features) || [] };
        if ((acc || this.shouldUpdateSourceInPlace()) && map.getSource(sourceId)) {
            map.getSource(sourceId).setData(collection);
        } else {
            removeSourceAndLayers(map, sourceId, this.getLayerIds());
            addOrUpdateGeoJsonSource(map, sourceId, collection);
            addLayersFn(map, sourceId);
        }
        this._finishRender(data, sourceId);
        return sourceId;
    }

    shouldUpdateSourceInPlace() { return false; }

    buildUrl() { throw new Error(this.id + ' must implement buildUrl()'); }
    getFetchOptions() { return this.config.getFetchOptions ? this.config.getFetchOptions() : {}; }
    beforeRequest() {}
    afterRequest() {}
    renderData(_data) { return null; }

    handleError(error) {
        if (this.config.onError) {
            this.config.onError(error, this);
            return;
        }
        if (typeof console !== 'undefined' && console.error) console.error(error);
    }

    clear() {
        this.abortPending();
        if (this.config.onRequestSettled) this.config.onRequestSettled(this);
        this.resetAccumulator();
        super.clear();
        if (this.config.onCleared) this.config.onCleared(this);
    }

    load() {
        if (!this.isActive() || !this.canActivate()) {
            if (this.config.onRequestStarted) this.config.onRequestStarted(this);
            this.clear();
            if (this.config.onRequestSettled) this.config.onRequestSettled(this);
            return;
        }

        this.abortPending();
        this.beforeRequest();
        const controller = new AbortController();
        const version = ++this.requestVersion;
        this.setAbortController(controller);
        this.loadState = 'loading';
        if (this.config.onRequestStarted) this.config.onRequestStarted(this);

        fetch(this.buildUrl(), { ...this.getFetchOptions(), signal: controller.signal })
            .then((response) => {
                if (response.ok) return response.json();
                return response.json()
                    .catch(() => null)
                    .then((data) => { throw responseError(response, data); });
            })
            .then((data) => {
                if (controller.signal.aborted || version !== this.requestVersion) return;
                this.renderData(data);
                this.loadState = 'success';
            })
            .catch((error) => {
                if (controller.signal.aborted || version !== this.requestVersion) return;
                if (error && error.name === 'AbortError') return;
                this.loadState = 'error';
                this.handleError(error);
            })
            .finally(() => {
                if (version === this.requestVersion) {
                    this.afterRequest();
                    if (this.config.onRequestSettled) this.config.onRequestSettled(this);
                }
                if (this.getAbortController() === controller) this.setAbortController(null);
            });
    }
}
